import React, { useEffect, useState } from "react";
import Dialog from "@mui/material/Dialog";
import { Form, Formik, FormikHelpers } from "formik";
import { array, object, string } from "yup";
import HoliDayCalenderForm from "../Layouts/HoliDayCalendarForm";
import { HoliDayCalenderFormValues } from "src/models/HoliDayCalender.model";
import {
  useEditHoliDayMutation,
  useViewHoliDayQuery,
} from "src/services/HolidayCalendarServices";
import { showToast } from "src/utils/showToaster";
import moment from "moment";
import { CircularProgress } from "@mui/material";

type Props = {
  onClose: () => void;
  holidayId: string;
};

const EditHolidayCalendarFormWrapper = ({ onClose, holidayId }: Props) => {
  //STATE
  const [holidayData, setHoliDayData] = useState<any>();

  //API
  const [editHoliDayCalendar] = useEditHoliDayMutation();
  const { data, isFetching, isLoading } = useViewHoliDayQuery(holidayId);

  //USEffect
  useEffect(() => {
    if (!isLoading && !isFetching) {
      setHoliDayData(data?.data);
    }
  }, [isFetching, isLoading, data]);

  // Combine year, month, and date and parse using Moment.js
  const combinedDate = `${holidayData?.date} ${holidayData?.month} ${holidayData?.year}`;
  const yourMomentObject = moment(combinedDate);

  // Form Initial Values
  const initialValues: HoliDayCalenderFormValues = {
    date: yourMomentObject,
    allBranches: holidayData?.allBranches || false ,
    optional: holidayData?.optional,
    holidayName: holidayData?.holidayName,
    branchId: holidayData?.branchId?.map((el: any) => {
      return {
        label: el?.branchName,
        value: el?._id,
      };
    }),
  };

  // Validation Schema
  const validationSchema = object().shape({
    date: string().required("Please enter date"),
    optional: string().required("Required"),
    holidayName: string().required("Holiday Name is Required"),
    branchId: array().required("branch  Name is Required"),
  });

  // Handle Submit
  const handleSubmit = (
    values: HoliDayCalenderFormValues,
    { setSubmitting, resetForm }: FormikHelpers<HoliDayCalenderFormValues>
  ) => {
    const myMomentObject = moment(values?.date);

    // Assuming myMomentObject is your Moment.js object
    const date = myMomentObject.date(); // Day of the month
    const month = myMomentObject.format("MMMM"); // Month (0-indexed, so January is 0)
    const year = myMomentObject.year(); // Full year

    const formattedValues = { 
      allBranches: values.allBranches,
      holidayName: values?.holidayName,
      branchId: values?.branchId?.map((branch: any) => branch?.value),
      date: date.toString(),
      month: month.toString(),
      year: year.toString(),
      optional: values?.optional,
    };
    editHoliDayCalendar({
      id: holidayId,
      body: formattedValues,
    }).then((res: any) => {
      if (res?.error) {
        showToast("error", res?.error?.data?.message);
      } else {
        if (res?.data?.status) {
          showToast("success", res?.data?.message);
          resetForm();
          onClose();
        } else {
          showToast("error", res?.data?.message);
        }
      }
      setSubmitting(false);
    });
  };

  return (
    <Dialog open maxWidth="xs" fullWidth>
      <Formik
        enableReinitialize
        initialValues={initialValues}
        validationSchema={validationSchema}
        onSubmit={handleSubmit}
      >
        {(formikProps) => (
          <Form>
            {(isLoading || isFetching) && (
              <div className="absolute w-[100%] h-[100%] flex justify-center items-center z-10 bg-slate-100 opacity-50">
                <CircularProgress />
              </div>
            )}
            <HoliDayCalenderForm
              formType="EDIT"
              formikProps={formikProps}
              onClose={onClose}
            />
          </Form>
        )}
      </Formik>
    </Dialog>
  );
};

export default EditHolidayCalendarFormWrapper;
